import { APIConfig } from "@/lib/api";
import { ApiError } from "@/lib/api/api-error";
import { request as __request } from "@/lib/api/request";
import { getCsrfToken } from "@/providers/auth";
import { type UserProfile, userProfileSchema } from "./types/userprofile";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

export class UserProfileService {
  /**
   * GET /api/me/profile/
   * - Returns the user profile (validated) or null if 401/404.
   * - Throws on unexpected errors.
   */
  static async getMine(): Promise<UserProfile | null> {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/me/",
          headers: {
            "X-CSRFToken": getCsrfToken() ?? "",
          },
        },
        userProfileSchema,
      );
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        // Treat unauthenticated/missing profile as "no profile"
        if (error.status === 403 || error.status === 404) {
          return null;
        }

        console.error(
          `[UserProfileService.getMine] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[UserProfileService.getMine] Unexpected error:", error);
      }
      // For resiliency, return null so the app can keep rendering
      return null;
    }
  }

  /**
   * PATCH /api/me/profile/
   * - Only updates has_completed_onboarding.
   * - Returns the updated, validated profile or throws on error.
   */
  static async setCompleted(flag: boolean): Promise<UserProfile | null> {
    try {
      const data = await __request(
        APIConfig,
        {
          method: "PATCH",
          url: "/api/v1/me/",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCsrfToken() ?? "",
          },
          body: { has_completed_onboarding: flag },
        },
        userProfileSchema,
      );
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[UserProfileService.setCompleted] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error(
          "[UserProfileService.setCompleted] Unexpected error:",
          error,
        );
      }
      return null;
    }
  }
}
